import { debounce, get, reduce } from 'lodash/fp';
// @ts-expect-error $TSFixMeError: Could not find a declaration file for module 'd3-selection'. '/home/pedro/projects/mews-js/node_modules/d3-selection/bui...
import { select as d3Select } from 'd3-selection';
import { TimeUnit, UnicodeEntity } from '@mews/enums';
import { TextKey } from '@mews-commander/enums';
import { DateTimeService } from '@mews/framework';
import { getContainer } from '@mews-commander/legacy-app-instance';
import {
    getReversedFullName,
    notNil,
    getChildTimeUnitPluralTextKey,
    getParentTimeUnitPluralTextKey,
} from '@mews-commander/utils';
import { DATE_LEGEND_HEIGHT, LINE_HEIGHT, RESOURCE_WIDTH, TEXT_PADDING, TOOLTIP_TIMEOUT } from '../../constants';
import { getCenter, round } from '../../utils/mathUtils';
import { isOtherService, isUseCluster } from '../../utils/services';
import { setTooltipText } from '../../tooltipStore';
import { CalendarEventExtended } from '../../types';
import { getEventsTooltipText } from '../../utils/calendarEvents';

export default function Tooltip() {
    const tooltip = d3Select('#tooltip');
    const debounceTooltip = debounce(TOOLTIP_TIMEOUT, create);
    let tooltipId: string | null = null;

    hide();
    return {
        showUse(
            xAxis: $TSFixMe,
            yAxis: $TSFixMe,
            viewport: $TSFixMe,
            use: $TSFixMe,
            timelineServiceId: string,
            timezone: string
        ) {
            const x = xAxis(getCenter(Math.max(use.minX, viewport.minX), Math.min(use.maxX, viewport.maxX)));
            const y = yAxis(use.minY);
            const content =
                isUseCluster(use) && use.isMultipleServices
                    ? getUseClusterText(use, timezone)
                    : getUseText(use, timelineServiceId, timezone);
            show(use.id, content, x, y);
        },
        showSpace(xAxis: $TSFixMe, yAxis: $TSFixMe, scale: $TSFixMe, space: $TSFixMe) {
            if (!space.isSlot) {
                const spaceWidth = round(RESOURCE_WIDTH * scale);
                const x = getCenter(space.minX * spaceWidth + LINE_HEIGHT, space.maxX * spaceWidth + LINE_HEIGHT);
                const y = yAxis(space.minY);
                show(space.id, getSpaceText(space), x, y);
            }
        },
        showEvents(x: number, events: CalendarEventExtended[], timezone: string) {
            const id = reduce((acc, e: CalendarEventExtended) => acc + e.id, '')(events);
            show(id, getEventsTooltipText(events, timezone), x, -LINE_HEIGHT);
        },
        showChangeViewToParent(x: number, y: number, timeUnit: TimeUnit) {
            show('parent', getChangeViewTooltipText(timeUnit, y), x, -LINE_HEIGHT * 2);
        },
        showChangeViewToChild(x: number, y: number, timeUnit: TimeUnit) {
            show('child', getChangeViewTooltipText(timeUnit, y), x, -LINE_HEIGHT);
        },
        hide,
    };

    function show(id: string, text: string, x: number, y: number) {
        if (tooltipId !== id) {
            hide();
            debounceTooltip({
                id,
                text,
                x,
                y,
            });
        }
    }

    function hide() {
        debounceTooltip.cancel();
        tooltipId = null;
        tooltip.style('opacity', 0).style('transform', 'scale(0)');
    }

    function getSpaceText(space: $TSFixMe) {
        return `${space.resourceCategoryType} ${space.name}\n${space.resourceCategoryName}`;
    }

    function formatDateRangeText(startDate: string, endDate: string) {
        return `${startDate} ${UnicodeEntity.Dash} ${endDate}`;
    }

    function formatOtherServiceUseText({ use, name, serviceLabel }: $TSFixMe) {
        const { serviceName } = use;
        return `${serviceLabel}${UnicodeEntity.Colon} ${serviceName}\n${name}`;
    }

    function formatClusterUseText({ cluster, serviceLabel, startDate, endDate }: $TSFixMe) {
        const { serviceNames } = cluster;
        return `${serviceLabel}${UnicodeEntity.Colon} ${serviceNames.join(', ')}
        \n${formatDateRangeText(startDate, endDate)}`;
    }

    function formatSingleDateTimeRangeText(startDate: string, startHour: string, endHour: string) {
        return `${startDate}\n${startHour} ${UnicodeEntity.Dash} ${endHour}`;
    }

    function formatDateTimeRangeText(startDate: string, endDate: string, startHour: string, endHour: string) {
        return `${startDate} ${startHour} ${UnicodeEntity.Dash} ${endDate} ${endHour}`;
    }

    function formatUseText({ name, startDate, endDate, startHour, endHour, serviceTimeUnit }: $TSFixMe) {
        if (serviceTimeUnit === TimeUnit.Hour) {
            return startDate === endDate
                ? `${name}\n${formatSingleDateTimeRangeText(startDate, startHour, endHour)}`
                : `${name}\n${formatDateTimeRangeText(startDate, endDate, startHour, endHour)}`;
        }
        return `${name}\n${formatDateRangeText(startDate, endDate)}`;
    }

    function getUseText(use: $TSFixMe, timelineServiceId: string, timezone: string) {
        const container = getContainer();
        const { appLocalizationComponent } = container();
        const {
            name: useName,
            isReservation,
            customer,
            startUtc: useStartUtc,
            endUtc: useEndUtc,
            serviceId,
            serviceTimeUnit,
        } = use;

        const name = isReservation ? getReversedFullName(customer) : useName;
        const startDate = appLocalizationComponent.localizeDate(DateTimeService.fromUtcString(useStartUtc, timezone));
        const endDate = appLocalizationComponent.localizeDate(DateTimeService.fromUtcString(useEndUtc, timezone));
        const startHour = appLocalizationComponent.localizeShortTime(
            DateTimeService.fromUtcString(useStartUtc, timezone)
        );
        const endHour = appLocalizationComponent.localizeShortTime(DateTimeService.fromUtcString(useEndUtc, timezone));
        const serviceLabel = appLocalizationComponent.localizeText(TextKey.Service);

        const useDetails = isOtherService(serviceId, timelineServiceId)
            ? formatOtherServiceUseText({ use, name, serviceLabel })
            : formatUseText({ use, name, startDate, endDate, startHour, endHour, serviceTimeUnit });

        const spaceDetails = notNil(get(['space', 'number'], use)) ? `\n${getSpaceText(use.space)}` : '';

        return useDetails + spaceDetails;
    }

    function getUseClusterText(cluster: $TSFixMe, timezone: string) {
        const container = getContainer();
        const { appLocalizationComponent } = container();
        const { startUtc: useStartUtc, endUtc: useEndUtc } = cluster;

        const startDate = appLocalizationComponent.localizeDate(DateTimeService.fromUtcString(useStartUtc, timezone));
        const endDate = appLocalizationComponent.localizeDate(DateTimeService.fromUtcString(useEndUtc, timezone));
        const serviceLabel = appLocalizationComponent.localizeText(TextKey.Service);

        const useDetails = formatClusterUseText({ cluster, serviceLabel, startDate, endDate });
        return useDetails;
    }

    function getChangeViewTooltipText(timeUnit: TimeUnit, y: number) {
        const container = getContainer();
        const { appLocalizationComponent } = container();

        if (y > LINE_HEIGHT && timeUnit === TimeUnit.Day) {
            return appLocalizationComponent.localizeText(TextKey.ClickToViewHoursOfThisDay);
        }

        const timeUnitTextKey =
            y > LINE_HEIGHT ? getChildTimeUnitPluralTextKey(timeUnit) : getParentTimeUnitPluralTextKey(timeUnit);
        const timeUnitText = appLocalizationComponent.localizeText(timeUnitTextKey).toLowerCase();
        const mainText = appLocalizationComponent.localizeText(TextKey.ClickToViewMore);
        return `${mainText} ${timeUnitText}`;
    }

    function create({ id, text, x, y }: { id: string; text: string; x: number; y: number }) {
        tooltipId = id;

        setTooltipText(text);

        const halfWidth = tooltip.node().clientWidth / 2;

        const yOffset: number = y > window.innerHeight / 2 ? tooltip.node().clientHeight : -LINE_HEIGHT;

        tooltip
            .style('z-index', 1)
            .style('opacity', null)
            .style('transform', null)
            .style('top', `${Math.max(Math.floor(y + DATE_LEGEND_HEIGHT - yOffset), TEXT_PADDING)}px`)
            .style('left', `${Math.max(Math.floor(x - halfWidth), TEXT_PADDING)}px`);
    }
}
